import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MobileLayout from '../../components/mobile/MobileLayout';
import MapConsent from '../../components/public/MapConsent';
import {
  eventDate, fmtDay, fmtDate, fmtTime,
  useUpcomingEvents, useContactInfo, clock,
  useWebsiteImages, heroPhotos, eventImageFor, eventSlot,
} from '../../lib/publicContent';
import { ed, useEditing, usePageMeta } from '../../lib/editTexts';

export default function MobileHome() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'de-DE';
  const [slide, setSlide] = useState(0);
  const timer = useRef(null);
  const editing = useEditing();

  const events = useUpcomingEvents(8);
  const { location, times, email, mapsUrl, mapsEmbedUrl } = useContactInfo();

  const slots = useWebsiteImages();
  const HERO_PHOTOS = heroPhotos(slots);
  usePageMeta('home');
  useEffect(() => {
    if (editing) return undefined;   // Bearbeitungsmodus: Slideshow steht
    timer.current = setInterval(() => setSlide(s => (s + 1) % HERO_PHOTOS.length), 5000);
    return () => clearInterval(timer.current);
  }, [HERO_PHOTOS.length, editing]);

  const serviceRows = [
    { key: 'home.day.sunday', time: clock(i18n.language, times?.sunday?.time || '16:30') },
    { key: 'home.day.thursday', time: clock(i18n.language, times?.thursday?.time || '19:00') },
  ];

  return (
    <MobileLayout>
      {/* HERO-SLIDESHOW */}
      <div style={{ position: 'relative', height: 260, overflow: 'hidden' }}>
        {HERO_PHOTOS.map((src, i) => (
          <div
            key={src}
            className="m-photo"
            data-slot="home.hero"
            data-slot-index={i}
            style={{
              position: 'absolute', inset: 0,
              backgroundImage: `linear-gradient(transparent 45%, rgba(20,12,8,0.5)), url("${src}")`,
              opacity: i === slide ? 1 : 0,
              pointerEvents: i === slide ? 'auto' : 'none',
              transition: 'opacity 1.2s ease',
            }}
          />
        ))}
        <div style={{ position: 'absolute', left: 20, right: 20, bottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span {...ed('home.slide_caption')} style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.95)' }}>
            {t('home.slide_caption')}
          </span>
          <span style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {HERO_PHOTOS.map((src, i) => (
              <button
                key={src}
                type="button"
                className={`m-dot${i === slide ? ' on' : ''}`}
                aria-label={`${t('home.slide_label')} ${i + 1}`}
                aria-current={i === slide}
                onClick={() => setSlide(i)}
              >
                <i />
              </button>
            ))}
          </span>
        </div>
      </div>

      {/* ROTER WILLKOMMENSBLOCK */}
      <section className="m-red" style={{ padding: '30px 22px 28px' }}>
        {/* Helles Wappen: Platz brand.logo_inverse gibt es im AgoraHub-Katalog noch nicht -> eingebautes Bild */}
        <img src="/kac-assets/LOGOw.png" alt={t('about.crest_alt')} style={{ height: 72, width: 'auto', display: 'block', marginBottom: 20 }} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.82)', marginBottom: 14 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
          <span {...ed('home.welcome_to')}>{t('home.welcome_to')}</span>
        </div>
        <h1 {...ed('home.title')} style={{ margin: 0, fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 34, lineHeight: 1.02, letterSpacing: '-0.02em' }}>
          {t('home.title')}
        </h1>
        <p {...ed('home.tagline')} style={{ margin: '16px 0 0', fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,0.9)' }}>
          {t('home.tagline')}
        </p>
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.24)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {serviceRows.map(({ key, time }) => (
            <div key={key}>
              <div {...ed(key)} style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.66)', marginBottom: 6 }}>{t(key)}</div>
              <div data-edit-central="contact_info" style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 22 }}>{time}</div>
            </div>
          ))}
        </div>
      </section>

      {/* EVENTS – Termine pflegt die Gemeinde im AgoraHub-Kalender */}
      <section className="m-sec">
        <div className="m-head">
          <span className="m-kicker" {...ed('home.events_kicker')}>{t('home.events_kicker')}</span>
          <h2 {...ed('home.upcoming_events')}>{t('home.upcoming_events')}</h2>
        </div>

        <div className="m-x" data-edit-central="events" style={{ margin: '0 -22px', padding: '0 22px 14px' }}>
          {(events.length ? events : Array(3).fill(null)).map((ev, idx) => {
            const isRed = idx % 5 === 4;
            const d = ev ? eventDate(ev) : '';
            const title = ev?.title || ev?.summary || '';
            return (
              <article
                key={ev?.id || ev?.event_id || idx}
                className={isRed ? '' : 'm-card'}
                style={{
                  flex: '0 0 232px', borderRadius: 6, overflow: 'hidden',
                  ...(isRed ? { background: '#C0291F', color: '#fff' } : {}),
                }}
              >
                <div className="m-photo" data-slot={eventSlot(ev)} style={{ height: 124, backgroundImage: `url("${eventImageFor(ev, slots)}")` }} />
                <div style={{ padding: '16px 18px 18px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: isRed ? 'rgba(255,255,255,0.85)' : '#C0291F', marginBottom: 8, minHeight: 18 }}>
                    {d ? `${fmtDay(d, locale)} · ${fmtDate(d, locale)}` : ' '}
                  </div>
                  <h3 style={{ margin: '0 0 12px', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 600, fontSize: 18, lineHeight: 1.15 }}>
                    {title || ' '}
                  </h3>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 12, color: isRed ? 'rgba(255,255,255,0.82)' : '#857A6E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev?.location || location?.addressLine1 || ''}
                    </span>
                    {d && (
                      <span style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                        {fmtTime(d, locale)}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* BESUCHE UNS */}
      <section className="m-sec">
        <div className="m-head">
          <span className="m-kicker" {...ed('home.visit_kicker')}>{t('home.visit_kicker')}</span>
          <h2 {...ed('home.visit_us')}>{t('home.visit_us')}</h2>
        </div>

        <div style={{ height: 180, border: '1px solid rgba(120,100,70,0.22)', borderRadius: 6, overflow: 'hidden', background: '#EDE4D5' }}>
          <MapConsent embedUrl={mapsEmbedUrl} mapsUrl={mapsUrl} height={180} />
        </div>

        <div data-edit-central="contact_info" style={{ marginTop: 20, fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 21, lineHeight: 1.28 }}>
          {location ? (
            <>{location.addressLine1}<br />{location.addressLine2}</>
          ) : ' '}
        </div>
        <a href={`mailto:${email}`} className="m-link" data-edit-central="contact_info" style={{ marginTop: 12, color: '#C0291F' }}>{email}</a>

        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="m-btn m-btn-red">
            <span {...ed('home.plan_route')}>{t('home.plan_route')}</span> →
          </a>
          <a href={`mailto:${email}`} className="m-btn m-btn-ghost" {...ed('home.contact')}>{t('home.contact')}</a>
        </div>
      </section>
    </MobileLayout>
  );
}
